import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { api } from "../../api/axios";
import { toast } from "react-hot-toast";
import Papa from "papaparse";
import {
  Table,
  Badge,
  Modal,
  Form,
  Button as BsButton,
  Tabs,
  Tab,
} from "react-bootstrap";
import { useCurrentTermAndSession } from "../../hooks/useCurrentTermAndSession";

const METHOD_LABELS = {
  bank_transfer: "Bank Transfer",
  cash: "Cash",
  online: "Online",
  paystack: "Paystack",
  "Bank Transfer": "Bank Transfer",
  Cash: "Cash",
  POS: "POS",
  Online: "Online",
};
const METHOD_COLORS = {
  paystack: "info",
  bank_transfer: "primary",
  cash: "secondary",
  online: "info",
  "Bank Transfer": "primary",
  Cash: "secondary",
  POS: "warning",
  Online: "info",
};

const STATUS_COLORS = {
  paid: "success",
  partial: "warning",
  issued: "primary",
  draft: "secondary",
};

function fmt(n) {
  return Number(n || 0).toLocaleString("en-NG");
}
function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function RecordPayments() {
  const { currentSession, currentTerm, isLoading: hookLoading } = useCurrentTermAndSession();
  const [isInitialized, setIsInitialized] = useState(false);

  const [session, setSession] = useState("");
  const [term, setTerm] = useState("");
  const [searchQ, setSearchQ] = useState("");

  useEffect(() => {
    if (currentSession && currentTerm && !isInitialized) {
      setSession(currentSession);
      setTerm(currentTerm);
      setIsInitialized(true);
    } else if (!hookLoading && !isInitialized) {
      setIsInitialized(true);
    }
  }, [currentSession, currentTerm, hookLoading, isInitialized]);

  const [bills, setBills] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);

  // CSV
  const [csvPreviewData, setCsvPreviewData] = useState([]);
  const [showCsvPreview, setShowCsvPreview] = useState(false);
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const fileInputRef = React.useRef(null);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [amountPaid, setAmountPaid] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("bank_transfer");
  const [bankName, setBankName] = useState("First Bank");
  const [reference, setReference] = useState("");
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [notes, setNotes] = useState("");

  const fetchBills = useCallback(async () => {
    try {
      // Fetch FamilyBills now
      const res = await api.get(
        `/bills/family?session=${session}&term=${term}`,
      );
      setBills((res.data.data || []).filter((b) => b.status !== "draft"));
    } catch {
      toast.error("Failed to load bills");
    }
  }, [session, term]);

  const fetchPayments = useCallback(async () => {
    try {
      const res = await api.get(`/payments?session=${session}`);
      setPayments(res.data.data || []);
    } catch {
      toast.error("Failed to load payments");
    }
  }, [session]);

  useEffect(() => {
    if (isInitialized && session && term) {
      fetchBills();
      fetchPayments();
    }
  }, [fetchBills, fetchPayments, isInitialized, session, term]);

  // ------ Record a manual payment ------
  const openModal = (bill) => {
    setSelectedBill(bill);
    setAmountPaid(bill.balance.toString());
    setReference(`Manual-${Math.floor(Math.random() * 100000)}`);
    setPaymentMethod("bank_transfer");
    setBankName("First Bank");
    setNotes("");
    setShowModal(true);
  };

  const handleRecord = async (e) => {
    e.preventDefault();
    setLoading(true);
    const num = Number(amountPaid);
    if (num <= 0) {
      toast.error("Amount must be > 0");
      setLoading(false);
      return;
    }
    if (num > selectedBill.balance) {
      toast.error("Exceeds outstanding balance!");
      setLoading(false);
      return;
    }

    try {
      await api.post("/payments", {
        familyBillId: selectedBill._id,
        parentId: selectedBill.parentId?._id || selectedBill.parentId,
        amountPaid: num,
        paymentMethod,
        bankName: paymentMethod === "bank_transfer" ? bankName : undefined,
        reference,
        paymentDate,
        notes: notes || undefined,
      });
      toast.success("Payment recorded!");
      setShowModal(false);
      fetchBills();
      fetchPayments();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to record payment");
    } finally {
      setLoading(false);
    }
  };

  // ------ CSV Upload Logic ------
  const handleDownloadTemplate = () => {
    if (outstandingBills.length === 0) {
      toast.error(
        "No outstanding bills found for this term to generate a template.",
      );
      return;
    }

    const headers = [
      "Parent ID",
      "Amount Paid",
      "Payment Method",
      "Payment Date",
      "Bank Name",
      "Reference",
      "Notes",
    ];
    const rows = [headers.join(",")];

    outstandingBills.forEach((b) => {
      const pid = b.parentId?.parentId || ""; // E.g., P004
      if (pid) {
        rows.push(`"${pid}","","","","","",""`);
      }
    });

    const csvContent = "data:text/csv;charset=utf-8," + rows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `Payments_Template_${session.replace("/", "-")}_${term}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCsvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: false,
      skipEmptyLines: true,
      complete: (results) => {
        const rows = results.data;
        if (rows.length <= 1) {
          toast.error("CSV file is empty or missing data rows.");
          return;
        }

        const parsedData = rows
          .slice(1)
          .map((row) => ({
            parentId:
              typeof row[0] === "string" ? row[0].trim() : String(row[0] || ""),
            amountPaid: Number(String(row[1] || "").replace(/,/g, "")),
            paymentMethod: row[2]?.trim() || "bank_transfer",
            paymentDate:
              row[3]?.trim() || new Date().toISOString().split("T")[0],
            bankName: row[4]?.trim() || "",
            reference: row[5]?.trim() || "",
            notes: row[6]?.trim() || "",
          }))
          .filter(
            (r) => r.parentId && !isNaN(r.amountPaid) && r.amountPaid > 0,
          );

        if (parsedData.length === 0) {
          toast.error("No valid payment records found in the file.");
          return;
        }

        setCsvPreviewData(parsedData);
        setShowCsvPreview(true);
      },
      error: (err) => {
        toast.error("Failed to parse CSV file: " + err.message);
      },
    });

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const submitCsvData = async () => {
    setUploadingCsv(true);
    try {
      const res = await api.post("/payments/bulk-csv", {
        data: csvPreviewData,
        session,
        term,
      });
      toast.success(
        `Successfully processed ${res.data.data.paymentsProcessed} payments for ${res.data.data.matchedParents} parents!`,
      );
      setShowCsvPreview(false);
      setCsvPreviewData([]);
      fetchBills();
      fetchPayments();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to upload bulk payments",
      );
    } finally {
      setUploadingCsv(false);
    }
  };

  // ------ Derived / filtered data ------
  const outstandingBills = bills.filter((b) => b.balance > 0);

  const filteredPayments = payments.filter((p) => {
    if (!searchQ) return true;
    const q = searchQ.toLowerCase();
    const name =
      `${p.parentId?.firstName} ${p.parentId?.lastName}`.toLowerCase();
    const ref = (p.reference || "").toLowerCase();
    return name.includes(q) || ref.includes(q);
  });

  const totalCollected = payments.reduce((s, p) => s + (p.amountPaid || 0), 0);
  const totalOutstanding = bills.reduce((s, b) => s + (b.balance || 0), 0);

  return (
    <Container>
      {/* ── Header ── */}
      <Header>
        <div
          className="d-flex justify-content-between align-items-center flex-wrap w-100 mb-3"
          style={{ gap: "15px" }}
        >
          <div>
            <h3>Payments</h3>
            <p className="mb-0" style={{ color: "#666" }}>
              Record manual payments against family bills and track history.
            </p>
          </div>
          <div className="d-flex gap-2">
            <input
              type="file"
              accept=".csv"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleCsvUpload}
            />
            <CsvBtn type="button" onClick={handleDownloadTemplate}>
              Download Template
            </CsvBtn>
            <CsvBtn
              primary
              type="button"
              onClick={() => fileInputRef.current?.click()}
            >
              Upload Bulk CSV
            </CsvBtn>
          </div>
        </div>
        <StatsRow>
          <StatCard color="#d4edda" textColor="#155724">
            <span>Total Collected</span>
            <strong>₦{fmt(totalCollected)}</strong>
          </StatCard>
          <StatCard color="#f8d7da" textColor="#721c24">
            <span>Total Outstanding</span>
            <strong>₦{fmt(totalOutstanding)}</strong>
          </StatCard>
        </StatsRow>
      </Header>

      {/* ── Filters ── */}
      <Controls>
        <SelectGroup>
          <label>Session</label>
          <select value={session} onChange={(e) => setSession(e.target.value)}>
            {["2023/2024", "2024/2025", "2025/2026", "2026/2027", "2027/2028", "2028/2029"].map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </SelectGroup>
        <SelectGroup>
          <label>Term</label>
          <select value={term} onChange={(e) => setTerm(e.target.value)}>
            {["FIRST TERM", "SECOND TERM", "THIRD TERM"].map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
        </SelectGroup>
        <SelectGroup style={{ flex: 2 }}>
          <label>Search</label>
          <input
            type="text"
            placeholder="Search by parent name or reference…"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
          />
        </SelectGroup>
      </Controls>

      {/* ── Tabs ── */}
      <Tabs defaultActiveKey="history" className="mb-3">
        {/* PAYMENT HISTORY */}
        <Tab eventKey="history" title={`All Payments (${payments.length})`}>
          <Card>
            {filteredPayments.length === 0 ? (
              <EmptyState>
                No payments recorded for this session yet.
              </EmptyState>
            ) : (
              <ScrollableTable>
                <Table responsive hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Parent</th>
                      <th>Parent Phone</th>
                      <th>Amount Paid</th>
                      <th>Mode</th>
                      <th>Bank</th>
                      <th>Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPayments.map((p) => (
                      <tr key={p._id}>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {fmtDate(p.paymentDate)}
                        </td>
                        <td style={{ whiteSpace: "nowrap", fontWeight: 600 }}>
                          {p.parentId?.firstName} {p.parentId?.lastName}
                        </td>
                        <td>
                          <code style={{ fontSize: 12 }}>
                            {p.parentId?.tel || "—"}
                          </code>
                        </td>
                        <td style={{ color: "#155724", fontWeight: 700 }}>
                          ₦{fmt(p.amountPaid)}
                        </td>
                        <td>
                          <Badge
                            bg={METHOD_COLORS[p.paymentMethod] || "secondary"}
                          >
                            {METHOD_LABELS[p.paymentMethod] || p.paymentMethod}
                          </Badge>
                        </td>
                        <td>{p.bankName || "—"}</td>
                        <td>
                          <small style={{ fontFamily: "monospace" }}>
                            {p.reference}
                          </small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ScrollableTable>
            )}
          </Card>
        </Tab>

        {/* OUTSTANDING BILLS */}
        <Tab
          eventKey="outstanding"
          title={`Outstanding Family Bills (${outstandingBills.length})`}
        >
          <Card>
            {outstandingBills.length === 0 ? (
              <EmptyState>
                🎉 No outstanding family bills for this term!
              </EmptyState>
            ) : (
              <ScrollableTable>
                <Table responsive hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Parent</th>
                      <th>Contact</th>
                      <th>Total Wards</th>
                      <th>Total Bill</th>
                      <th>Paid</th>
                      <th>Balance</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {outstandingBills.map((b) => (
                      <tr key={b._id}>
                        <td style={{ fontWeight: 600 }}>
                          {b.parentId?.firstName} {b.parentId?.lastName}
                        </td>
                        <td>
                          <code style={{ fontSize: 12 }}>
                            {b.parentId?.tel || "—"}
                          </code>
                        </td>
                        <td>{b.parentId?.wards?.length || 0}</td>
                        <td>₦{fmt(b.totalAmount)}</td>
                        <td style={{ color: "#155724" }}>
                          ₦{fmt(b.amountPaid)}
                        </td>
                        <td style={{ color: "#721c24", fontWeight: 700 }}>
                          ₦{fmt(b.balance)}
                        </td>
                        <td>
                          <Badge bg={STATUS_COLORS[b.status] || "secondary"}>
                            {b.status?.toUpperCase()}
                          </Badge>
                        </td>
                        <td>
                          <BsButton
                            size="sm"
                            variant="success"
                            onClick={() => openModal(b)}
                          >
                            Record Payment
                          </BsButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ScrollableTable>
            )}
          </Card>
        </Tab>
      </Tabs>

      {/* ── Record Payment Modal ── */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Record Manual Payment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBill && (
            <BillSummary>
              <strong>
                {selectedBill.parentId?.firstName}{" "}
                {selectedBill.parentId?.lastName}
              </strong>
              <span className="text-muted"> · Parent</span>
              <div className="mt-2 d-flex gap-4 flex-wrap">
                <span>
                  Total Bill: <strong>₦{fmt(selectedBill.totalAmount)}</strong>
                </span>
                <span>
                  Paid:{" "}
                  <strong style={{ color: "#155724" }}>
                    ₦{fmt(selectedBill.amountPaid)}
                  </strong>
                </span>
                <span>
                  Balance:{" "}
                  <strong style={{ color: "#721c24" }}>
                    ₦{fmt(selectedBill.balance)}
                  </strong>
                </span>
              </div>
            </BillSummary>
          )}

          <Form onSubmit={handleRecord}>
            <div className="row g-3">
              <div className="col-sm-6">
                <Form.Group>
                  <Form.Label>Amount Paid (₦)</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max={selectedBill?.balance}
                    value={amountPaid}
                    onChange={(e) => setAmountPaid(e.target.value)}
                    required
                  />
                  <Form.Text className="text-muted">
                    Max: ₦{fmt(selectedBill?.balance)}
                  </Form.Text>
                </Form.Group>
              </div>

              <div className="col-sm-6">
                <Form.Group>
                  <Form.Label>Payment Method</Form.Label>
                  <Form.Select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    required
                  >
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cash">Cash</option>
                    <option value="paystack">Paystack (manual)</option>
                    <option value="online">Other Online</option>
                  </Form.Select>
                </Form.Group>
              </div>

              {paymentMethod === "bank_transfer" && (
                <div className="col-sm-6">
                  <Form.Group>
                    <Form.Label>Bank Name</Form.Label>
                    <Form.Select
                      value={bankName}
                      onChange={(e) => setBankName(e.target.value)}
                    >
                      <option>First Bank</option>
                      <option>Sterling Bank</option>
                      <option>Jaiz Bank</option>
                    </Form.Select>
                  </Form.Group>
                </div>
              )}

              <div className="col-sm-6">
                <Form.Group>
                  <Form.Label>Payment Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    required
                  />
                </Form.Group>
              </div>

              <div
                className={
                  paymentMethod === "bank_transfer" ? "col-12" : "col-sm-6"
                }
              >
                <Form.Group>
                  <Form.Label>Transaction Reference / Teller No.</Form.Label>
                  <Form.Control
                    type="text"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    required
                  />
                </Form.Group>
              </div>

              <div className="col-12">
                <Form.Group>
                  <Form.Label>
                    Notes <span className="text-muted">(optional)</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g. Parent called to confirm transfer…"
                  />
                </Form.Group>
              </div>
            </div>

            <BsButton
              variant="success"
              type="submit"
              disabled={loading}
              className="w-100 mt-4"
            >
              {loading ? "Recording…" : "Confirm & Record Payment"}
            </BsButton>
          </Form>
        </Modal.Body>
      </Modal>

      {/* ── CSV Preview Modal ── */}
      <Modal
        show={showCsvPreview}
        onHide={() => setShowCsvPreview(false)}
        size="lg"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Preview CSV Upload</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            Please review the mapped payments before committing them to the
            database.
          </p>
          <div
            style={{
              maxHeight: "450px",
              overflowY: "auto",
              border: "1px solid #eee",
              borderRadius: "8px",
            }}
          >
            <Table responsive hover size="sm" className="mb-0">
              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  background: "#fff",
                  zIndex: 1,
                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                }}
              >
                <tr>
                  <th>Parent ID</th>
                  <th>Amount Paid (₦)</th>
                  <th>Method</th>
                  <th>Date</th>
                  <th>Reference</th>
                </tr>
              </thead>
              <tbody>
                {csvPreviewData.map((row, idx) => (
                  <tr key={idx}>
                    <td style={{ fontWeight: 600 }}>{row.parentId}</td>
                    <td style={{ color: "#155724", fontWeight: 700 }}>
                      {fmt(row.amountPaid)}
                    </td>
                    <td>
                      <Badge
                        bg={METHOD_COLORS[row.paymentMethod] || "secondary"}
                      >
                        {METHOD_LABELS[row.paymentMethod] || row.paymentMethod}
                      </Badge>
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {fmtDate(row.paymentDate)}
                    </td>
                    <td>
                      <small style={{ fontFamily: "monospace", color: "#666" }}>
                        {row.reference || "Auto-generated"}
                      </small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <BsButton
            variant="light"
            onClick={() => setShowCsvPreview(false)}
            disabled={uploadingCsv}
          >
            Cancel
          </BsButton>
          <BsButton
            variant="primary"
            onClick={submitCsvData}
            disabled={uploadingCsv}
          >
            {uploadingCsv
              ? "Uploading..."
              : `Confirm & Upload ${csvPreviewData.length} Payment${csvPreviewData.length === 1 ? "" : "s"
              }`}
          </BsButton>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

// ── Styled Components ─────────────────────────────────────────────────────────
const Container = styled.div`
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

const CsvBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(p) => (p.primary ? "#1a73e8" : "#fff")};
  color: ${(p) => (p.primary ? "#fff" : "#1a73e8")};
  border: 1px solid ${(p) => (p.primary ? "#1a73e8" : "#d2e3fc")};

  &:hover {
    background: ${(p) => (p.primary ? "#1557b0" : "#f0f4fc")};
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(26, 115, 232, 0.15);
  }

  &:active {
    transform: translateY(0);
  }
`;

const StatsRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const StatCard = styled.div`
  background: ${(p) => p.color};
  color: ${(p) => p.textColor};
  padding: 12px 18px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  span {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
  }
  strong {
    font-size: 20px;
  }
`;

const Controls = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
  background: white;
  padding: 14px 18px;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
`;

const SelectGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 160px;
  label {
    font-size: 13px;
    font-weight: 600;
    color: #555;
  }
  select,
  input {
    padding: 9px 13px;
    border-radius: 7px;
    border: 1px solid #ddd;
    font-size: 14px;
    width: 100%;
  }
`;

const Card = styled.div`
  background: white;
  padding: 16px;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  margin-bottom: 10px;
`;

const ScrollableTable = styled.div`
  max-height: 520px;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 4px;
  }
`;

const EmptyState = styled.div`
  padding: 40px;
  text-align: center;
  color: #888;
  font-size: 15px;
  background: #f9f9f9;
  border-radius: 8px;
`;

const BillSummary = styled.div`
  background: #f0f4ff;
  border: 1px solid #c3cfe2;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 20px;
  font-size: 15px;
`;
